import React from 'react';
import { Box, Chip, Container, IconButton, Typography } from '@mui/material';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import { ProductService } from '../services/productService';
import { FavoritesService } from '../services/favoritesService';
import { useAuthStore } from '../store/authStore';
import toast from 'react-hot-toast';
import { supabase } from '../lib/supabase';

export type PageProductsStripSectionConfig = {
  enabled?: boolean;
  heading?: string;
  subtitle?: string;
  limit?: number;
  sortBy?: 'created_at' | 'favorite_count' | 'view_count' | 'price';
  sortOrder?: 'asc' | 'desc';
  badgeText?: string;
  radius?: number; // px
  productIds?: string[];
  tags?: string[];
  nameKeywords?: string[];
};

type Props = {
  config?: PageProductsStripSectionConfig | null;
};

const clamp = (n: any, min: number, max: number) => Math.max(min, Math.min(max, Number(n) || 0));

const formatEur = (v: any) => {
  const num = Number(v);
  if (!Number.isFinite(num)) return '';
  try {
    return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(num);
  } catch {
    return `${num.toFixed(2)} €`;
  }
};

const PageProductsStripSection: React.FC<Props> = ({ config }) => {
  const cfg: PageProductsStripSectionConfig = (config && typeof config === 'object') ? config : {};

  const heading = typeof cfg.heading === 'string' ? cfg.heading : 'Deals des Tages';
  const subtitle = typeof cfg.subtitle === 'string' ? cfg.subtitle : '';
  const limit = clamp(cfg.limit, 2, 30) || 12;
  const sortBy = (cfg.sortBy === 'favorite_count' || cfg.sortBy === 'view_count' || cfg.sortBy === 'price') ? cfg.sortBy : 'created_at';
  const sortOrder = cfg.sortOrder === 'asc' ? 'asc' : 'desc';
  const badgeText = typeof cfg.badgeText === 'string' ? cfg.badgeText : '';
  const radius = clamp(cfg.radius, 0, 32);
  const productIds = Array.isArray(cfg.productIds) ? cfg.productIds.map((x) => String(x || '').trim()).filter(Boolean) : [];
  const tags = Array.isArray(cfg.tags) ? cfg.tags.map((x) => String(x || '').trim()).filter(Boolean) : [];
  const nameKeywords = Array.isArray(cfg.nameKeywords) ? cfg.nameKeywords.map((x) => String(x || '').trim()).filter(Boolean) : [];

  const { user, isAuthenticated } = useAuthStore();

  const [loading, setLoading] = React.useState<boolean>(false);
  const [items, setItems] = React.useState<any[]>([]);
  const [favorites, setFavorites] = React.useState<Set<string>>(new Set());
  const [favBusy, setFavBusy] = React.useState<Set<string>>(new Set());

  React.useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        // 1) Manual list of product IDs wins and preserves order
        if (productIds.length > 0) {
          const minimal = await ProductService.getProductsByIds(productIds);
          if (!alive) return;
          setItems(
            minimal.map((p: any) => ({
              id: p.id,
              name: p.name,
              price: p.price,
              images: [p.image],
              is_job: false,
              is_active: true,
            }))
          );
          return;
        }

        // 2) Flexible filtering: tags overlap + keywords OR (name ilike)
        let query = supabase
          .from('products')
          .select('id,name,price,images,tags,is_active,is_job,created_at,view_count,favorite_count')
          .eq('is_active', true)
          .eq('is_job', false);

        if (tags.length > 0) {
          const normalized = tags.map((t) => t.toLowerCase()).filter(Boolean);
          // overlap operator on Postgres arrays
          query = (query as any).overlaps('tags', normalized);
        }

        if (nameKeywords.length > 0) {
          const ks = nameKeywords.map((k) => k.trim()).filter(Boolean);
          if (ks.length > 0) {
            const cond = ks.map((k) => `name.ilike.%${k.replace(/,/g, ' ').trim()}%`).join(',');
            query = query.or(cond);
          }
        }

        query = query.order(sortBy, { ascending: sortOrder === 'asc' }).limit(limit);
        const { data, error } = await query;
        if (error) throw error;
        const products = Array.isArray(data) ? data : [];
        if (!alive) return;
        setItems(products);
      } catch (e: any) {
        console.error('Products strip load failed', e);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [limit, sortBy, sortOrder, productIds.join('|'), tags.join('|'), nameKeywords.join('|')]);

  React.useEffect(() => {
    let alive = true;
    (async () => {
      if (!isAuthenticated || !user?.id) {
        if (!alive) return;
        setFavorites(new Set());
        return;
      }
      if (!items.length) return;
      try {
        const ids = items.map((p: any) => String(p?.id || '')).filter(Boolean);
        const statuses = await FavoritesService.getFavoriteStatuses(ids, user.id);
        const next = new Set<string>();
        Object.entries(statuses || {}).forEach(([id, val]) => { if (val) next.add(id); });
        if (!alive) return;
        setFavorites(next);
      } catch (e) {
        console.error('Failed to load favorite statuses', e);
      }
    })();
    return () => { alive = false; };
  }, [isAuthenticated, user?.id, items]);

  React.useEffect(() => {
    const handler = (ev: any) => {
      try {
        const id = String(ev?.detail?.id || '');
        if (!id) return;
        const isFav = Boolean(ev?.detail?.isFavorited);
        setFavorites((prev) => {
          const next = new Set(prev);
          if (isFav) next.add(id); else next.delete(id);
          return next;
        });
      } catch {}
    };
    window.addEventListener('favorite-changed' as any, handler);
    return () => window.removeEventListener('favorite-changed' as any, handler);
  }, []);

  const toggleFavorite = async (productId: string) => {
    if (!isAuthenticated || !user?.id) {
      toast.error('Bitte einloggen um Favoriten zu nutzen');
      return;
    }
    if (favBusy.has(productId)) return;
    setFavBusy(prev => new Set(prev).add(productId));
    try {
      const isNow = await FavoritesService.toggleFavorite(productId, user.id);
      setFavorites((prev) => {
        const next = new Set(prev);
        if (isNow) next.add(productId); else next.delete(productId);
        return next;
      });
      window.dispatchEvent(new CustomEvent('favorite-changed', { detail: { id: productId, isFavorited: isNow, isJob: false } }));
    } catch (e: any) {
      const details = e?.message || e?.error_description || e?.details || e?.hint || String(e);
      toast.error(`Favorit fehlgeschlagen: ${details}`);
    } finally {
      setFavBusy(prev => {
        const next = new Set(prev);
        next.delete(productId);
        return next;
      });
    }
  };

  if (cfg.enabled === false) return null;

  return (
    <Box sx={{ py: { xs: 5, md: 7 } }}>
      <Container maxWidth="lg">
        <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 2, mb: 2.25, flexWrap: 'wrap' }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 950, letterSpacing: -0.2 }}>
              {heading}
            </Typography>
            {subtitle ? (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {subtitle}
              </Typography>
            ) : null}
          </Box>
        </Box>

        <Box
          sx={{
            display: 'flex',
            gap: 2,
            overflowX: 'auto',
            pb: 1,
            pr: 1,
            scrollSnapType: 'x mandatory',
            WebkitOverflowScrolling: 'touch',
            '&::-webkit-scrollbar': { height: 8 },
            '&::-webkit-scrollbar-thumb': { backgroundColor: 'rgba(2,6,23,0.14)', borderRadius: 999 },
            '&::-webkit-scrollbar-track': { backgroundColor: 'rgba(2,6,23,0.06)', borderRadius: 999 },
          }}
        >
          {(loading && items.length === 0) ? (
            <Box sx={{ border: '1px dashed', borderColor: 'divider', borderRadius: 3, p: 3, minWidth: 260, bgcolor: 'background.paper' }}>
              <Typography variant="body2" color="text.secondary">Lade Produkte…</Typography>
            </Box>
          ) : items.length === 0 ? (
            <Box sx={{ border: '1px dashed', borderColor: 'divider', borderRadius: 3, p: 3, minWidth: 260, bgcolor: 'background.paper' }}>
              <Typography variant="body2" color="text.secondary">Keine Produkte gefunden.</Typography>
            </Box>
          ) : (
            items.map((p: any) => {
              const id = String(p?.id || '');
              const img = Array.isArray(p?.images) && p.images.length > 0 ? String(p.images[0]) : '/placeholder-product.jpg';
              const name = String(p?.name || '');
              const price = formatEur(p?.price);
              const isFav = favorites.has(id);
              const busy = favBusy.has(id);

              return (
                <Box
                  key={id}
                  sx={{
                    scrollSnapAlign: 'start',
                    minWidth: 220,
                    maxWidth: 220,
                  }}
                >
                  <Box
                    sx={{
                      position: 'relative',
                      borderRadius: radius,
                      overflow: 'visible',
                      bgcolor: 'grey.100',
                      border: '1px solid',
                      borderColor: 'divider',
                      boxShadow: '0 16px 46px rgba(2,6,23,0.10)',
                      aspectRatio: '4 / 3',
                    }}
                  >
                    <Box
                      sx={{
                        position: 'absolute',
                        inset: 0,
                        borderRadius: radius,
                        overflow: 'hidden',
                      }}
                    >
                      <Box component="img" src={img} alt={name} sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                    </Box>

                    {badgeText ? (
                      <Chip
                        label={badgeText}
                        size="small"
                        sx={{
                          position: 'absolute',
                          left: 10,
                          bottom: 10,
                          fontWeight: 900,
                          bgcolor: 'rgba(0,0,0,0.72)',
                          color: 'white',
                          borderRadius: 999,
                          px: 0.5,
                        }}
                      />
                    ) : null}

                    <IconButton
                      onClick={() => toggleFavorite(id)}
                      disabled={busy}
                      sx={{
                        position: 'absolute',
                        right: 10,
                        top: 10,
                        zIndex: 3,
                        bgcolor: 'rgba(255,255,255,0.92)',
                        color: isFav ? 'error.main' : 'text.secondary',
                        '&:hover': { bgcolor: 'rgba(255,255,255,1)' },
                      }}
                    >
                      {isFav ? <FavoriteIcon /> : <FavoriteBorderIcon />}
                    </IconButton>
                  </Box>

                  <Box sx={{ pt: 1.25 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 900,
                        lineHeight: 1.2,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }}
                    >
                      {name}
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 0.5, fontWeight: 950 }}>
                      {price}
                    </Typography>
                  </Box>
                </Box>
              );
            })
          )}
        </Box>
      </Container>
    </Box>
  );
};

export default PageProductsStripSection;
